import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CreditEatMode } from '@models/subaccount/credit-movements';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { PrefixLabels, SubaccountService } from '@services/subaccount.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTime, getCurrencySymbol } from '@utils/utils';
import { Location } from '@angular/common';
import * as _ from 'lodash';

@Component({
  selector: 'wsb-subaccount-history',
  templateUrl: './reseller-subaccount-history.component.html',
  styleUrls: ['./reseller-subaccount-history.component.scss'],
  standalone: false
})
export class ResellerSubaccountHistoryComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;
  @ViewChild('modalDelete') modalDelete;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  // modal
  modalDeleteModal: NgbModalRef;

  // table
  tableColumnDefs = null;
  tableGridOptions = null;
  paginationPageSize = 10;

  // idSubaccount
  idSubaccountUser: string;
  creditEatModeSubUser: string;
  idPurchase: number;

  labelAvailablecredits = 'subaccounts_history_availablecredits';

  constructor(
    private activateRoute: ActivatedRoute,
    private subaccountService: SubaccountService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private atlasToast: AtlasToastService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.activateRoute.queryParams.subscribe(params => {
      const key = 'id';
      const param = params[key];
      this.idSubaccountUser = param !== null && param.length > 0 ? param : null;
    });

    const that = this;
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'common_date',
        field: 'dateTime',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? formateDateTime(params.data.dateTime) : '')
        }
      },
      {
        headerNameTranslate: 'common_from',
        field: 'fromLogin',
        sortable: false
      },
      {
        headerNameTranslate: 'pages_list_choose_dates_to',
        field: 'toLogin',
        sortable: false
      },
      {
        headerNameTranslate: 'settings_subaccounts_movecredit_transfer',
        field: 'credits',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? getCurrencySymbol() + ' ' + params.data.credits : '')
        }
      },
      {
        headerNameTranslate: 'common_type',
        field: 'type',
        sortable: false,
        suppressSizeToFit: true,
        cellRenderer: ppm => {
          return this.getLabelsType(ppm.data);
        },
        cellStyle: {
          'line-height': '20px'
        }
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        resizable: false,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          hide: pack => {
            return pack ? this.isHideDeleteCreditMovment(pack) : false;
          },
          items: [
            {
              label: 'common_delete',
              onClick: e => this.openModalDelete(e)
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];
    // pagination
    this.tableGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      rowHeight: 115,
      // autoHeight: true,
      loadFunction: this.idSubaccountUser !== null ? this.reloadList.bind(that) : null,
      noRowsOverlayOptions: {
        // message: ''
      }
    });
  }

  isHideDeleteCreditMovment(pack) {
    const cem = pack.creditEatMode;
    const avCredits = Number.parseFloat(pack.availableCredits);
    if (cem === CreditEatMode.REAL_CREDITS || avCredits === 0) {
      return true;
    } else {
      return pack.pricePerMessage ? false : true;
    }
  }

  private getLabelsType(data?: any): string {
    if (data) {
      const cem = data.creditEatMode;
      if (cem === CreditEatMode.VIRTUAL_CREDITS) {
        if (data.pricePerMessage) {
          return this.getLabelsVirtualCredits(data);
        } else {
          return this.getLabelsRealCredits(data);
        }
      } else if (cem === CreditEatMode.REAL_CREDITS) {
        return this.getLabelsRealCredits(data);
      }
      return '';
    }

    return '';
  }

  // old customer
  private getLabelsRealCredits(data?: any): string {
    let result = '';
    if (data) {
      const mt = data.idMessageType ? data.idMessageType.toLowerCase() : '';
      result = result + this.getLabelPricePerMessage(mt);
    }
    return result;
  }

  private getLabelsVirtualCredits(data?: any): string {
    let result = '';
    if (data) {
      const ppm = data.pricePerMessage;
      const availableCredits = data.availableCredits;
      result = result + this.getLabelsTranslate(this.labelAvailablecredits) + ' ' + availableCredits + ' ' + getCurrencySymbol() + '<br>';
      ppm.forEach(p => {
        const mt = p.messageType.toLowerCase();
        const up = p.unitPrice;
        result = result + this.getLabelPricePerMessage(mt, up) + '<br>';
      });
    }
    return result;
  }

  private getLabelPricePerMessage(label: string, item?: string): string {
    const valueLabel = label ? this.getLabelsTranslate(PrefixLabels.CREDIT_TYPE + label) : '';
    // label real credits
    if (!item) {
      return valueLabel;
    }
    // label virtual credits
    return valueLabel + ': ' + item + ' ' + getCurrencySymbol();
  }

  private getLabelsTranslate(label: string) {
    let labels;
    this.translateService.get(label).subscribe(l => {
      labels = l;
    });
    return labels;
  }

  reloadList(startRow?, endRow?, sort?) {
    return this.subaccountService.listCreditMovements(
      this.idSubaccountUser,
      1 + startRow / (endRow - startRow),
      endRow - startRow,
      sort && sort.length ? (sort[0].colId === 'dateTime' ? 'RECORDING_DATE' : null) : null,
      sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
    );
  }

  openModalDelete(data?: any): void {
    this.idPurchase = data.idPurchase;
    this.modalDeleteModal = this.modalService.open(this.modalDelete);
    this.modalDeleteModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteForm() {
    const that = this;
    this.subaccountService.deleteCreditMovement(Number(this.idSubaccountUser), this.idPurchase).subscribe(
      () => {
        this.atlasToast.success('common_success', null);
        that.grid.refresh();
      },
      () => {
        this.atlasToast.error('common_error', null);
        that.grid.refresh();
      },
      () => {}
    );
    this.modalDeleteModal.close();
  }

  goBack() {
    this.location.back();
  }
}
